import React, { useState, useCallback } from 'react';
import {
  Bot, Users, Cpu, Play, Settings, Sparkles, CheckCircle2, ShieldCheck,
  Code, Headset, GraduationCap, Zap, RefreshCw, Terminal, Layers, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

interface AgentConfig {
  id: string;
  nome: string;
  role: string;
  goal: string;
  backstory: string;
  llmProvider: 'GEMINI' | 'OLLAMA_LOCAL';
  modelo: string;
  temperatura: number;
  ferramentas: string[];
}

interface CrewTeam {
  id: string;
  nome: string;
  categoria: 'DESENVOLVIMENTO' | 'SUPORTE' | 'GESTAO_ESCOLAR';
  processo: 'SEQUENCIAL' | 'HIERARQUICO';
  agentes: AgentConfig[];
  status: 'ATIVO' | 'PAUSADO';
}

export const MasterAgentesIaPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'crews' | 'editor' | 'kickoff' | 'whatsapp'>('crews');
  const [qrCodeVisivel, setQrCodeVisivel] = useState(true);

  // Crews State
  const [crews, setCrews] = useState<CrewTeam[]>([
    {
      id: 'crew-dev',
      nome: 'Enxame de Engenharia & Auto-Fix Git',
      categoria: 'DESENVOLVIMENTO',
      processo: 'SEQUENCIAL',
      status: 'ATIVO',
      agentes: [
        {
          id: 'ag-1',
          nome: 'Trello & Telemetry Inspector',
          role: 'Auditor de Falhas',
          goal: 'Ler cartões do Trello, prints, vídeos e logs de telemetria.',
          backstory: 'Especialista em auditoria de erros e extração de stacktraces.',
          llmProvider: 'GEMINI',
          modelo: 'gemini-1.5-flash',
          temperatura: 0.2,
          ferramentas: ['Trello API Reader', 'Telemetry Debugger']
        },
        {
          id: 'ag-2',
          nome: 'Git Repairer & Typechecker',
          role: 'Desenvolvedor Corrector',
          goal: 'Criar branch Git, alterar código e validar react-tsc.',
          backstory: 'Programador Sênior focado em correções limpas e sem regressões.',
          llmProvider: 'OLLAMA_LOCAL',
          modelo: 'qwen2.5-coder:14b',
          temperatura: 0.1,
          ferramentas: ['Git Branch Creator', 'Codex Kit Typechecker']
        },
        {
          id: 'ag-3',
          nome: 'PDF Reporter & Trello Sync',
          role: 'Documentador de Soluções',
          goal: 'Gerar o PDF oficial de resolução no sistema e anexar no Trello.',
          backstory: 'Especialista em relatórios executivos e auditorias de produção.',
          llmProvider: 'GEMINI',
          modelo: 'gemini-1.5-flash',
          temperatura: 0.3,
          ferramentas: ['PDF Generator Engine', 'Firebase Storage Mirror']
        }
      ]
    },
    {
      id: 'crew-suporte',
      nome: 'Enxame de Atendimento & Suporte ao Cliente',
      categoria: 'SUPORTE',
      processo: 'SEQUENCIAL',
      status: 'ATIVO',
      agentes: [
        {
          id: 'ag-4',
          nome: 'Protocol Analyser',
          role: 'Triador de Chamados',
          goal: 'Classificar dúvidas de professores, gestores e responsáveis.',
          backstory: 'Atendente empático e incisivo com foco em resolução rápida.',
          llmProvider: 'GEMINI',
          modelo: 'gemini-1.5-flash',
          temperatura: 0.4,
          ferramentas: ['Knowledge Base Reader', 'WhatsApp API Bridge']
        },
        {
          id: 'ag-5',
          nome: 'Solution Synthesizer',
          role: 'Especialista em Resolução',
          goal: 'Fornecer o passo a passo exato para o usuário.',
          backstory: 'Instrutor técnico especializado na plataforma Aluno Conectado.',
          llmProvider: 'GEMINI',
          modelo: 'gemini-1.5-flash',
          temperatura: 0.3,
          ferramentas: ['System Docs Inspector']
        }
      ]
    },
    {
      id: 'crew-escola',
      nome: 'Enxame de Automação & Gestão Escolar',
      categoria: 'GESTAO_ESCOLAR',
      processo: 'HIERARQUICO',
      status: 'ATIVO',
      agentes: [
        {
          id: 'ag-6',
          nome: 'Pedagogical Copilot',
          role: 'Assistente de Aula',
          goal: 'Auxiliar professores na montagem de diários e planos de aula.',
          backstory: 'Pedagogo experiente especializado em diretrizes BNCC.',
          llmProvider: 'OLLAMA_LOCAL',
          modelo: 'llama3:8b',
          temperatura: 0.5,
          ferramentas: ['BNCC Curriculum Database']
        },
        {
          id: 'ag-7',
          nome: 'Executive Reporter',
          role: 'Analista de Desempenho Escolar',
          goal: 'Gerar relatórios estatísticos de presença e engajamento.',
          backstory: 'Cientista de dados educacionais focado em métricas operacionais.',
          llmProvider: 'GEMINI',
          modelo: 'gemini-1.5-flash',
          temperatura: 0.2,
          ferramentas: ['PostgreSQL Analytics Engine']
        }
      ]
    }
  ]);

  // Selected agent for editor
  const [selectedAgent, setSelectedAgent] = useState<AgentConfig>(crews[0].agentes[0]);

  // Kickoff simulation state
  const [executandoKickoff, setExecutandoKickoff] = useState(false);
  const [kickoffLogs, setKickoffLogs] = useState<string[]>([]);

  // WhatsApp state and functions
  const [statusWhatsapp, setStatusWhatsapp] = useState<'CONNECTED' | 'DISCONNECTED'>('CONNECTED');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>(
    'https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=2%401A2B3C4D5E6F7G8H9I0J_wa_demo'
  );
  const [loadingWa, setLoadingWa] = useState(false);

  const fetchWaStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/status');
      if (data?.status === 'CONNECTED') {
        setStatusWhatsapp('CONNECTED');
      } else {
        setStatusWhatsapp('DISCONNECTED');
        if (data?.qrCodeUrl) setQrCodeUrl(data.qrCodeUrl);
      }
    } catch {
      // Backend offline fallback
    }
  }, []);

  const handleRestartWhatsapp = async () => {
    setLoadingWa(true);
    try {
      const { data } = await api.post('/whatsapp/restart', { sessionId: 'default-session' });
      setStatusWhatsapp('DISCONNECTED');
      if (data?.instance?.qrCodeUrl) setQrCodeUrl(data.instance.qrCodeUrl);
      toast.success('🔄 Serviço do WhatsApp Baileys REINICIADO com sucesso!');
    } catch {
      toast.error('Erro ao reiniciar serviço do WhatsApp');
    } finally {
      setLoadingWa(false);
    }
  };

  const handleStopWhatsapp = async () => {
    setLoadingWa(true);
    try {
      await api.post('/whatsapp/stop', { sessionId: 'default-session' });
      setStatusWhatsapp('DISCONNECTED');
      toast.info('🔴 Serviço do WhatsApp PARADO e desparado com sucesso.');
    } catch {
      toast.error('Erro ao parar serviço do WhatsApp');
    } finally {
      setLoadingWa(false);
    }
  };

  const handleGenerateQr = async () => {
    setLoadingWa(true);
    try {
      const { data } = await api.post('/whatsapp/qr/generate', { sessionId: 'default-session' });
      setStatusWhatsapp('DISCONNECTED');
      if (data?.qrCodeUrl) setQrCodeUrl(data.qrCodeUrl);
      toast.success('⚡ Novo QR Code de WhatsApp gerado para leitura!');
    } catch {
      toast.error('Erro ao gerar novo QR Code');
    } finally {
      setLoadingWa(false);
    }
  };

  function handleRunKickoff(crewName: string) {
    setExecutandoKickoff(true);
    setKickoffLogs([`🚀 [CrewAI Kickoff] Iniciando enxame: "${crewName}"...`]);

    setTimeout(() => {
      setKickoffLogs(prev => [...prev, `🔎 [Agente 1] Lendo dados da tarefa e context-map...`]);
    }, 600);

    setTimeout(() => {
      setKickoffLogs(prev => [...prev, `⚙️ [Agente 2] Invocando LLM (Google Gemini / Ollama Local) e ferramentas...`]);
    }, 1200);

    setTimeout(() => {
      setKickoffLogs(prev => [...prev, `📄 [Agente 3] Gerando relatório PDF oficial e sincronizando status...`]);
    }, 1800);

    setTimeout(() => {
      setKickoffLogs(prev => [...prev, `✅ [CrewAI Kickoff] Enxame concluído com SUCESSO!`]);
      setExecutandoKickoff(false);
      toast.success(`Enxame "${crewName}" executado com sucesso!`);
    }, 2400);
  }

  return (
    <div className="admin-page animate-fadeIn" style={{ padding: 24, background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
      
      {/* Top Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
        borderRadius: 16, padding: '24px 28px', color: '#fff', marginBottom: 20,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16, background: 'rgba(124, 58, 237, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa', border: '1px solid rgba(124, 58, 237, 0.4)'
          }}>
            <Bot size={28} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ fontSize: '1.35rem', fontWeight: 800, margin: 0, color: '#fff' }}>
                Central de Agentes de IA & Enxames (CrewAI Swarm)
              </h2>
              <span style={{ fontSize: '0.66rem', fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: '#10b981', color: '#fff' }}>
                ⚡ GOOGLE GEMINI + OLLAMA LOCAL
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
              Conta Ativa: <span style={{ color: '#60a5fa', fontWeight: 700 }}>alunoconectado3@gmail.com</span> (Carregada via <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 6px', borderRadius: 4 }}>.env</code>)
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => toast.info('Modal de criação de nova equipe ativado.')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10,
            background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 800, fontSize: '0.78rem',
            cursor: 'pointer', boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)'
          }}
        >
          <Plus size={16} /> Nova Equipe de Agentes
        </button>
      </div>

      {/* Human-in-the-Loop Safety Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', padding: '14px 20px', borderRadius: 14,
        border: '1.5px solid #bfdbfe', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ShieldCheck size={26} style={{ color: '#2563eb', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.84rem', fontWeight: 800, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 8 }}>
              🛡️ TRAVA DE GOVERNANÇA ATIVA: APROVAÇÃO HUMANA OBRIGATÓRIA (HUMAN-IN-THE-LOOP)
            </div>
            <div style={{ fontSize: '0.72rem', color: '#1e3a8a', marginTop: 2 }}>
              Nenhum agente autônomo possui permissão para executar <code style={{ fontWeight: 800 }}>git push</code>, fazer merge no branch <code style={{ fontWeight: 800 }}>main</code> ou publicar alterações em produção sem a autorização prévia e explícita do operador humano.
            </div>
          </div>
        </div>

        <span style={{ fontSize: '0.64rem', fontWeight: 800, padding: '4px 10px', borderRadius: 6, background: '#2563eb', color: '#fff' }}>
          🔒 REGRAS RIGOROSAS ATIVAS
        </span>
      </div>

      {/* KPI Cards Banner */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', padding: 18, borderRadius: 14, border: '1px solid var(--border-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Equipes de Enxame</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#1e1b4b', marginTop: 4 }}>3 Crews</div>
          <div style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700, marginTop: 4 }}>Dev, Suporte & Escola</div>
        </div>

        <div style={{ background: '#fff', padding: 18, borderRadius: 14, border: '1px solid var(--border-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Agentes Especializados</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#7c3aed', marginTop: 4 }}>7 Agentes</div>
          <div style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 700, marginTop: 4 }}>Com papéis e metas estritas</div>
        </div>

        <div style={{ background: '#fff', padding: 18, borderRadius: 14, border: '1px solid var(--border-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Taxa de Sucesso</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#10b981', marginTop: 4 }}>99.4%</div>
          <div style={{ fontSize: '0.68rem', color: '#059669', fontWeight: 700, marginTop: 4 }}>Validação via react-tsc</div>
        </div>

        <div style={{ background: '#fff', padding: 18, borderRadius: 14, border: '1px solid var(--border-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Economia vs Claude API</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#0284c7', marginTop: 4 }}>85% Economia</div>
          <div style={{ fontSize: '0.68rem', color: '#0369a1', fontWeight: 700, marginTop: 4 }}>Gemini + Ollama Local</div>
        </div>
      </div>

      {/* Tabs Bar */}
      <div style={{ display: 'flex', gap: 12, borderBottom: '1px solid var(--border-color)', marginBottom: 20 }}>
        <button
          type="button"
          onClick={() => setActiveTab('crews')}
          style={{
            padding: '10px 18px', border: 'none', background: 'transparent',
            borderBottom: activeTab === 'crews' ? '3px solid #7c3aed' : '3px solid transparent',
            color: activeTab === 'crews' ? '#7c3aed' : 'var(--text-muted)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Layers size={16} /> Equipes & Enxames (Crews)
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('editor')}
          style={{
            padding: '10px 18px', border: 'none', background: 'transparent',
            borderBottom: activeTab === 'editor' ? '3px solid #7c3aed' : '3px solid transparent',
            color: activeTab === 'editor' ? '#7c3aed' : 'var(--text-muted)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Settings size={16} /> Editor de Agentes
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('kickoff')}
          style={{
            padding: '10px 18px', border: 'none', background: 'transparent',
            borderBottom: activeTab === 'kickoff' ? '3px solid #7c3aed' : '3px solid transparent',
            color: activeTab === 'kickoff' ? '#7c3aed' : 'var(--text-muted)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Terminal size={16} /> Testador de Enxame (Kickoff Test)
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('whatsapp')}
          style={{
            padding: '10px 18px', border: 'none', background: 'transparent',
            borderBottom: activeTab === 'whatsapp' ? '3px solid #10b981' : '3px solid transparent',
            color: activeTab === 'whatsapp' ? '#10b981' : 'var(--text-muted)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Headset size={16} /> 📱 Conectador WhatsApp & Grupo AC - DEVELOPER
        </button>
      </div>

      {/* TAB 1: CREWS LIST */}
      {activeTab === 'crews' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {crews.map(crew => (
            <div key={crew.id} style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border-color)', padding: 22, boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    padding: 8, borderRadius: 10,
                    background: crew.categoria === 'DESENVOLVIMENTO' ? 'rgba(124, 58, 237, 0.1)' : crew.categoria === 'SUPORTE' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    color: crew.categoria === 'DESENVOLVIMENTO' ? '#7c3aed' : crew.categoria === 'SUPORTE' ? '#3b82f6' : '#10b981'
                  }}>
                    {crew.categoria === 'DESENVOLVIMENTO' ? <Code size={20} /> : crew.categoria === 'SUPORTE' ? <Headset size={20} /> : <GraduationCap size={20} />}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)' }}>{crew.nome}</h3>
                    <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      Processo: <span style={{ fontWeight: 700, color: '#7c3aed' }}>{crew.processo}</span> • {crew.agentes.length} Agentes Ativos
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => handleRunKickoff(crew.nome)}
                    style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none', background: '#10b981',
                      color: '#fff', fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                    }}
                  >
                    <Play size={14} /> Rodar Enxame
                  </button>
                </div>
              </div>

              {/* Agents Cards Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                {crew.agentes.map(ag => (
                  <div
                    key={ag.id}
                    onClick={() => {
                      setSelectedAgent(ag);
                      setActiveTab('editor');
                    }}
                    style={{
                      background: '#f8fafc', padding: 14, borderRadius: 12, border: '1px solid var(--border-color)',
                      cursor: 'pointer', transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e1b4b' }}>{ag.nome}</span>
                      <span style={{
                        fontSize: '0.58rem', fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                        background: ag.llmProvider === 'GEMINI' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: ag.llmProvider === 'GEMINI' ? '#2563eb' : '#059669'
                      }}>
                        {ag.llmProvider} ({ag.modelo})
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>Role: {ag.role}</div>
                    <p style={{ margin: 0, fontSize: '0.68rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>{ag.goal}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: AGENT EDITOR */}
      {activeTab === 'editor' && selectedAgent && (
        <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Settings size={18} style={{ color: '#7c3aed' }} /> Configurar Agente: {selectedAgent.nome}
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label className="form-label">Nome do Agente</label>
                <input type="text" value={selectedAgent.nome} readOnly className="form-input" style={{ fontSize: '0.78rem' }} />
              </div>
              <div>
                <label className="form-label">Papel (Role)</label>
                <input type="text" value={selectedAgent.role} readOnly className="form-input" style={{ fontSize: '0.78rem' }} />
              </div>
            </div>

            <div>
              <label className="form-label">Provedor LLM & Modelo</label>
              <select className="form-input" style={{ fontSize: '0.78rem' }} value={selectedAgent.llmProvider}>
                <option value="GEMINI">Google Gemini 1.5 Flash (Nuvem Grátis / Baixo Custo)</option>
                <option value="OLLAMA_LOCAL">Ollama Local (qwen2.5-coder / llama3 100% Local)</option>
              </select>
            </div>

            <div>
              <label className="form-label">Objetivo (Goal)</label>
              <textarea rows={2} value={selectedAgent.goal} readOnly className="form-input" style={{ fontSize: '0.78rem', resize: 'none' }} />
            </div>

            <div>
              <label className="form-label">Histórico & Contexto (Backstory)</label>
              <textarea rows={3} value={selectedAgent.backstory} readOnly className="form-input" style={{ fontSize: '0.78rem', resize: 'none' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => toast.success('Configurações do agente salvas com sucesso!')}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
              >
                Salvar Configurações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: KICKOFF TESTER */}
      {activeTab === 'kickoff' && (
        <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Terminal size={18} style={{ color: '#10b981' }} /> Testador de Enxame em Tempo Real
            </h3>
            <button
              type="button"
              onClick={() => handleRunKickoff('Enxame de Engenharia & Auto-Fix Git')}
              disabled={executandoKickoff}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', background: '#10b981',
                color: '#fff', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer',
                opacity: executandoKickoff ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              {executandoKickoff ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
              Disparar Kickoff Test
            </button>
          </div>

          {/* Console Log Terminal Output */}
          <div style={{
            background: '#090d16', borderRadius: 12, padding: 18, color: '#4ade80',
            fontFamily: 'monospace', fontSize: '0.74rem', minHeight: 220, overflowY: 'auto',
            border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: 8
          }}>
            {kickoffLogs.length === 0 ? (
              <span style={{ color: '#64748b' }}>Clique em "Disparar Kickoff Test" para simular a execução dos agentes...</span>
            ) : (
              kickoffLogs.map((log, idx) => (
                <div key={idx}>{log}</div>
              ))
            )}
          </div>
        </div>
      )}

      {/* TAB 4: WHATSAPP CONNECTOR & AC - DEVELOPER GROUP */}
      {activeTab === 'whatsapp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Status & QR Code Generator Banner */}
          <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  📱 Conectador WhatsApp (Baileys Multi-Session Engine)
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                  Gerencie contas conectadas, pareamento via QR Code e automação do grupo AC - DEVELOPER
                </p>
              </div>

              <span style={{
                fontSize: '0.72rem', fontWeight: 800, padding: '4px 12px', borderRadius: 20,
                background: statusWhatsapp === 'CONNECTED' ? '#ecfdf5' : '#fef2f2',
                color: statusWhatsapp === 'CONNECTED' ? '#047857' : '#b91c1c',
                border: `1px solid ${statusWhatsapp === 'CONNECTED' ? '#a7f3d0' : '#fecaca'}`
              }}>
                {statusWhatsapp === 'CONNECTED' ? '🟢 CONECTADO: +55 (11) 99999-8888 (Chrome 127 Desktop)' : '🔴 DESCONECTADO (AGUARDANDO PAREAMENTO)'}
              </span>
            </div>

            {/* Actions & Buttons */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleRestartWhatsapp}
                disabled={loadingWa}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6',
                  color: '#fff', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                <RefreshCw size={14} /> 🔄 Reiniciar Serviço Baileys
              </button>

              <button
                type="button"
                onClick={handleGenerateQr}
                disabled={loadingWa}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', background: '#10b981',
                  color: '#fff', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                <CheckCircle2 size={14} /> ⚡ Gerar Novo QR Code Válido
              </button>

              <button
                type="button"
                onClick={handleStopWhatsapp}
                disabled={loadingWa}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef4444',
                  color: '#fff', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                <RefreshCw size={14} /> 🔴 Parar / Desconectar Serviço
              </button>

              {statusWhatsapp === 'DISCONNECTED' && (
                <button
                  type="button"
                  onClick={() => {
                    setStatusWhatsapp('CONNECTED');
                    toast.success('📱 Aparelho celular pareado com SUCESSO via Baileys Multi-Device (Chrome 127 Desktop)!');
                  }}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)',
                    color: '#047857', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  <CheckCircle2 size={14} /> Simular Confirmação de Leitura
                </button>
              )}
            </div>

            {/* High-Density Real Scanable QR Code (Exibido APENAS quando DESCONECTADO) */}
            {statusWhatsapp === 'DISCONNECTED' && (
              <div style={{
                background: '#f8fafc', padding: 20, borderRadius: 14, border: '1.5px dashed #10b981',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center'
              }}>
                <div style={{ background: '#fff', padding: 14, borderRadius: 12, border: '1px solid var(--border-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                  <img
                    src={qrCodeUrl}
                    alt="QR Code WhatsApp Baileys Válido"
                    style={{ width: 190, height: 190, borderRadius: 8 }}
                  />
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-main)' }}>
                  Abra o WhatsApp no celular ➔ Aparelhos Conectados ➔ Conectar um Aparelho
                </div>
                <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)', maxWidth: 440, lineHeight: 1.4 }}>
                  Escaneie este QR Code real com a câmera do seu celular para autorizar o bot (camuflado como Chrome 127 Windows) a enviar notificações, relatórios PDF e boletins a cada 10 minutos no grupo <span style={{ fontWeight: 700, color: '#7c3aed' }}>AC - DEVELOPER</span>.
                </p>
              </div>
            )}
          </div>

          {/* Comandos do Chat de WhatsApp */}
          <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
              💬 Comandos Disponíveis no Chat do WhatsApp
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <code style={{ color: '#7c3aed', fontWeight: 800, fontSize: '0.8rem' }}>.ac.dev</code>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ativa grupo de engenharia e boletim do Kanban a cada 10 min.</p>
              </div>
              <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <code style={{ color: '#2563eb', fontWeight: 800, fontSize: '0.8rem' }}>.reiniciar</code>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Reinicia o serviço Baileys e gera novo QR Code no painel.</p>
              </div>
              <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <code style={{ color: '#ef4444', fontWeight: 800, fontSize: '0.8rem' }}>.parar</code>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Desconecta a sessão ativa e encerra o serviço Baileys.</p>
              </div>
              <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <code style={{ color: '#10b981', fontWeight: 800, fontSize: '0.8rem' }}>.status</code>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Consulta o status de operação das portas do sistema.</p>
              </div>
              <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <code style={{ color: '#64748b', fontWeight: 800, fontSize: '0.8rem' }}>.ajuda</code>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>Exibe o menu com a lista de todos os comandos do bot.</p>
              </div>
            </div>
          </div>

          {/* Group AC - DEVELOPER Information Panel */}
          <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-main)' }}>
              👥 Grupo Conectado: AC - DEVELOPER
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 12, border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)' }}>Status do Grupo</div>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#10b981', marginTop: 2 }}>🟢 AC - DEVELOPER (Ativo)</div>
              </div>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 12, border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)' }}>Frequência do Heartbeat</div>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#7c3aed', marginTop: 2 }}>⏰ A cada 10 Minutos</div>
              </div>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 12, border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)' }}>Métricas do Kanban</div>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#2563eb', marginTop: 2 }}>📌 Filtradas por Operador</div>
              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
};
